import { useEffect, useRef, useState } from "react";

// --- Colors (matching app's Desert Rose theme score colors) ---
const C = {
  blue: "#5b8bd4",
  gold: "#d4a95b",
  green: "#5bd47b",
} as const;

// --- Constants (matching Remotion demo) ---
const SCORE_LABELS = ["1", "2", "3", "4", "5"] as const;

const VOTE_CATEGORIES = [
  { label: "Divertido", color: C.blue, selectedIndex: 3 },
  { label: "Confiável", color: C.gold, selectedIndex: 4 },
  { label: "Atraente", color: C.green, selectedIndex: 3 },
] as const;

const RESULTS_SCORES = [
  { label: "Divertido", value: 7.8, max: 10, color: C.blue },
  { label: "Confiável", value: 8.4, max: 10, color: C.gold },
  { label: "Atraente", value: 7.6, max: 10, color: C.green },
] as const;

const PHOTO1 = { overall: 7.9, scores: [{ label: "Divertido", v: 7.8 }, { label: "Confiável", v: 8.4 }, { label: "Atraente", v: 7.6 }] };
const PHOTO2 = { overall: 8.5, scores: [{ label: "Divertido", v: 8.2 }, { label: "Confiável", v: 8.7 }, { label: "Atraente", v: 8.5 }] };

// Scene durations in ms
const TIMING = {
  voting: 7000,
  results: 5000,
  comparison: 6000,
} as const;

const TOTAL_DURATION = TIMING.voting + TIMING.results + TIMING.comparison;

// --- Hook: elapsed time that loops ---
function useLoopTimer(duration: number) {
  const [elapsed, setElapsed] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(0);

  useEffect(() => {
    startRef.current = performance.now();

    const tick = (now: number) => {
      const e = (now - startRef.current) % duration;
      setElapsed(e);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration]);

  return elapsed;
}

// --- Easing ---
function easeOut(t: number) {
  return 1 - (1 - Math.min(1, Math.max(0, t))) ** 3;
}

function clamp01(t: number) {
  return Math.min(1, Math.max(0, t));
}

function withAlpha(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// --- Main Component ---
export default function ProductDemo() {
  const elapsed = useLoopTimer(TOTAL_DURATION);

  let scene: "voting" | "results" | "comparison";
  let sceneElapsed: number;

  if (elapsed < TIMING.voting) {
    scene = "voting";
    sceneElapsed = elapsed;
  } else if (elapsed < TIMING.voting + TIMING.results) {
    scene = "results";
    sceneElapsed = elapsed - TIMING.voting;
  } else {
    scene = "comparison";
    sceneElapsed = elapsed - TIMING.voting - TIMING.results;
  }

  return (
    <div className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-xl sm:rounded-2xl">
      <div className="aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/9]">
        {scene === "voting" && <VotingScene elapsed={sceneElapsed} />}
        {scene === "results" && <ResultsScene elapsed={sceneElapsed} />}
        {scene === "comparison" && <ComparisonScene elapsed={sceneElapsed} />}
      </div>
    </div>
  );
}

// ===================== VOTING SCENE =====================
function VotingScene({ elapsed }: { elapsed: number }) {
  const photoProgress = easeOut(elapsed / 400);
  const fadeOut = elapsed > 6500 ? 1 - clamp01((elapsed - 6500) / 500) : 1;
  const submitProgress = easeOut(clamp01((elapsed - 5500) / 300));

  return (
    <div
      className="flex h-full items-center justify-center gap-4 px-4 py-4 sm:gap-8 sm:px-8 sm:py-6"
      style={{ opacity: fadeOut }}
    >
      {/* Photo card — constrained width so selectors have room */}
      <div
        className="relative h-[75%] max-w-[40%] shrink-0"
        style={{
          opacity: photoProgress,
          transform: `scale(${0.9 + 0.1 * photoProgress})`,
        }}
      >
        <img
          src="/selfie-1.png"
          alt=""
          className="h-full w-full rounded-xl object-contain sm:rounded-2xl"
        />
      </div>

      {/* Score selectors + submit */}
      <div className="flex flex-col items-center gap-3 sm:gap-5">
        {/* 3 vertical columns side by side */}
        <div className="flex gap-3 sm:gap-5">
          {VOTE_CATEGORIES.map((cat, i) => (
            <ScoreSelectorAnimated
              key={cat.label}
              label={cat.label}
              color={cat.color}
              selectedIndex={cat.selectedIndex}
              appearDelay={500 + i * 200}
              tapTime={2500 + i * 1000}
              elapsed={elapsed}
            />
          ))}
        </div>

        {/* Submit button */}
        <div
          className="rounded-lg bg-primary px-8 py-2 text-sm font-semibold text-primary-foreground sm:rounded-xl sm:px-12 sm:py-3 sm:text-base"
          style={{
            opacity: submitProgress,
            transform: `scale(${0.8 + 0.2 * submitProgress})`,
          }}
        >
          Enviar
        </div>
      </div>
    </div>
  );
}

function ScoreButton({
  scoreLabel,
  isSelected,
  bounceScale,
  color,
}: {
  scoreLabel: string;
  isSelected: boolean;
  bounceScale: number;
  color: string;
}) {
  return (
    <div
      className="flex h-[34px] w-[38px] items-center justify-center rounded-lg text-sm font-semibold sm:h-[42px] sm:w-[46px] sm:rounded-xl sm:text-base"
      style={{
        color: isSelected ? color : "var(--muted-foreground)",
        backgroundColor: isSelected ? withAlpha(color, 0.2) : "var(--muted)",
        border: isSelected ? `2px solid ${color}` : "2px solid transparent",
        transform: `scale(${bounceScale})`,
        transition: "background-color 0.2s, border-color 0.2s, color 0.2s",
      }}
    >
      {scoreLabel}
    </div>
  );
}

function ScoreSelectorAnimated({
  label,
  color,
  selectedIndex,
  appearDelay,
  tapTime,
  elapsed,
}: {
  label: string;
  color: string;
  selectedIndex: number;
  appearDelay: number;
  tapTime: number;
  elapsed: number;
}) {
  const appear = easeOut(clamp01((elapsed - appearDelay) / 300));
  const isTapped = elapsed >= tapTime;

  return (
    <div
      className="flex flex-col items-center gap-1.5"
      style={{
        opacity: appear,
        transform: `translateY(${(1 - appear) * 20}px)`,
      }}
    >
      <div className="flex flex-col gap-1">
        {[...SCORE_LABELS].reverse().map((scoreLabel) => {
          const btnIndex = SCORE_LABELS.indexOf(scoreLabel);
          const isSelected = isTapped && btnIndex === selectedIndex;
          const tapBounce = isSelected ? easeOut(clamp01((elapsed - tapTime) / 200)) : 0;
          const bounceScale = isSelected ? 1 + 0.08 * Math.sin(tapBounce * Math.PI) : 1;

          return (
            <ScoreButton
              key={scoreLabel}
              scoreLabel={scoreLabel}
              isSelected={isSelected}
              bounceScale={bounceScale}
              color={color}
            />
          );
        })}
      </div>
    </div>
  );
}

// ===================== RESULTS SCENE =====================
function ResultsScene({ elapsed }: { elapsed: number }) {
  const cardAppear = easeOut(clamp01(elapsed / 400));
  const fadeOut = elapsed > 4500 ? 1 - clamp01((elapsed - 4500) / 500) : 1;

  return (
    <div className="flex h-full items-center justify-center px-3 py-3 sm:p-8" style={{ opacity: fadeOut }}>
      <div
        className="w-full max-w-md rounded-xl border border-border bg-card p-3 sm:rounded-2xl sm:p-6"
        style={{
          opacity: cardAppear,
          transform: `scale(${0.92 + 0.08 * cardAppear})`,
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg sm:h-12 sm:w-12 sm:rounded-xl">
            <img src="/selfie-1.png" alt="" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-foreground sm:text-base">Sua foto de Encontros</p>
            <p className="text-[10px] text-muted-foreground sm:text-xs">25 avaliações recebidas</p>
          </div>
          <div
            className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:px-3 sm:py-1 sm:text-xs"
            style={{
              transform: `scale(${easeOut(clamp01((elapsed - 300) / 300))})`,
              color: C.blue,
              backgroundColor: withAlpha(C.blue, 0.1),
              border: `1px solid ${withAlpha(C.blue, 0.4)}`,
            }}
          >
            Concluida
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-2.5 sm:mt-4">
          <div className="mb-1 flex justify-between text-[10px] text-muted-foreground sm:text-xs">
            <span>Progresso</span>
            <span>25/25</span>
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-muted sm:h-1.5">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${easeOut(clamp01((elapsed - 500) / 600)) * 100}%` }}
            />
          </div>
        </div>

        {/* Score bars */}
        <div className="mt-2.5 flex flex-col gap-2 sm:mt-4 sm:gap-3">
          {RESULTS_SCORES.map((score, i) => {
            const barDelay = 800 + i * 300;
            const fillProgress = easeOut(clamp01((elapsed - barDelay) / 600));
            const barWidth = (score.value / score.max) * 100 * fillProgress;
            const valueOpacity = clamp01((fillProgress - 0.5) * 2);

            return (
              <div key={score.label} className="flex items-center gap-1.5 sm:gap-3">
                <span
                  className="w-14 text-right text-[10px] font-medium sm:w-20 sm:text-sm"
                  style={{ color: score.color }}
                >
                  {score.label}
                </span>
                <div
                  className="relative h-4 flex-1 overflow-hidden rounded sm:h-7 sm:rounded-lg"
                  style={{ backgroundColor: withAlpha(score.color, 0.1) }}
                >
                  <div
                    className="absolute inset-y-0 left-0 rounded sm:rounded-lg"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: score.color,
                      opacity: 0.35,
                    }}
                  />
                </div>
                <span
                  className="w-7 text-xs font-bold sm:w-8 sm:text-base"
                  style={{ color: score.color, opacity: valueOpacity }}
                >
                  {score.value.toFixed(1)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ===================== COMPARISON SCENE =====================
function ComparisonScene({ elapsed }: { elapsed: number }) {
  const titleAppear = easeOut(clamp01(elapsed / 300));
  const selectionTime = 3500;
  const selectionProgress = clamp01((elapsed - selectionTime) / 400);
  const checkScale = easeOut(clamp01((elapsed - selectionTime - 300) / 200));
  const labelAppear = easeOut(clamp01((elapsed - selectionTime - 500) / 300));

  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5 px-3 py-3 sm:gap-4 sm:p-8">
      <div className="flex items-start gap-2 sm:gap-4 md:gap-6">
        {/* Photo 1 */}
        <ComparisonCard
          imageSrc="/selfie-1.png"
          label="Foto 1"
          overall={PHOTO1.overall}
          scores={PHOTO1.scores}
          isWinner={false}
          elapsed={elapsed}
          delay={100}
          selectionProgress={selectionProgress}
          checkScale={checkScale}
        />

        {/* VS badge */}
        <div
          className="flex items-center self-center rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground sm:px-3 sm:py-1.5 sm:text-xs"
          style={{ opacity: titleAppear }}
        >
          vs
        </div>

        {/* Photo 2 (winner) */}
        <div className="flex flex-col items-center gap-1">
          <ComparisonCard
            imageSrc="/selfie-2.png"
            label="Foto 2"
            overall={PHOTO2.overall}
            scores={PHOTO2.scores}
            isWinner={true}
            elapsed={elapsed}
            delay={200}
            selectionProgress={selectionProgress}
            checkScale={checkScale}
          />
          <div
            className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold sm:px-3 sm:py-1 sm:text-xs"
            style={{
              opacity: labelAppear,
              transform: `translateY(${(1 - labelAppear) * 6}px)`,
              color: C.green,
              backgroundColor: withAlpha(C.green, 0.1),
              border: `1px solid ${C.green}`,
            }}
          >
            Melhor foto
          </div>
        </div>
      </div>
    </div>
  );
}

function ComparisonCard({
  imageSrc,
  label,
  overall,
  scores,
  isWinner,
  elapsed,
  delay,
  selectionProgress,
  checkScale,
}: {
  imageSrc: string;
  label: string;
  overall: number;
  scores: { label: string; v: number }[];
  isWinner: boolean;
  elapsed: number;
  delay: number;
  selectionProgress: number;
  checkScale: number;
}) {
  const cardAppear = easeOut(clamp01((elapsed - delay) / 400));
  const scoreAppear = easeOut(clamp01((elapsed - 1000 - delay) / 400));
  const dimOpacity = !isWinner ? 1 - selectionProgress * 0.4 : 1;
  const borderColor = isWinner && selectionProgress > 0.5 ? C.green : "var(--border)";

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-[min(35vw,160px)] overflow-hidden rounded-lg border-2 sm:rounded-xl"
        style={{
          opacity: cardAppear * dimOpacity,
          transform: `scale(${0.85 + 0.15 * cardAppear})`,
          borderColor,
          transition: "border-color 0.3s",
        }}
      >
        <div className="relative aspect-[3/4]">
          <img src={imageSrc} alt="" className="h-full w-full rounded-t-lg object-contain sm:rounded-t-xl" />
          {/* Overall score badge */}
          <div
            className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-bold backdrop-blur sm:bottom-2 sm:right-2 sm:rounded-lg sm:px-2.5 sm:py-1 sm:text-base"
            style={{
              opacity: scoreAppear,
              color: isWinner ? C.green : C.gold,
            }}
          >
            {overall.toFixed(1)}
          </div>

          {/* Winner checkmark */}
          {isWinner && selectionProgress > 0.5 && (
            <div
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full sm:right-2 sm:top-2 sm:h-8 sm:w-8"
              style={{ transform: `scale(${checkScale})`, backgroundColor: C.green }}
            >
              <svg className="h-3 w-3 sm:h-3.5 sm:w-3.5" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="var(--background)" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>

        {/* Score rows */}
        <div className="flex flex-col gap-px p-1.5 sm:gap-1 sm:p-2.5">
          {scores.map((s) => (
            <div
              key={s.label}
              className="flex items-center justify-between"
              style={{ opacity: scoreAppear }}
            >
              <span className="text-[8px] text-muted-foreground sm:text-xs">{s.label}</span>
              <span className="text-[8px] font-semibold text-foreground sm:text-xs">{s.v.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[9px] text-muted-foreground sm:text-xs" style={{ opacity: cardAppear }}>
        {label}
      </p>
    </div>
  );
}
